"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Heart } from "lucide-react";
import { ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";

/** Misma clave que usa /cashback para pintar sus corazones. */
const FAV_KEY = "sucom:fav-stores";

function syncLocal(storeId: string, favorite: boolean) {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const ids = new Set<string>(raw ? JSON.parse(raw) : []);
    if (favorite) ids.add(storeId);
    else ids.delete(storeId);
    localStorage.setItem(FAV_KEY, JSON.stringify([...ids]));
  } catch {}
}

/**
 * Corazón sobre el logo de la tienda para guardarla en favoritos de la cuenta
 * (`UserFavoriteStore`). Sin sesión, lleva a iniciar sesión y regresa aquí.
 */
export function FavoriteStoreHeart({ storeId, storeName }: { storeId: string; storeName: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const [favorite, setFavorite] = useState(false);
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/api/profile/favorites/stores", { cache: "no-store" })
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 401) {
          setLoggedIn(false);
          return;
        }
        const json = (await res.json().catch(() => null)) as { data?: { storeId: string }[] } | null;
        setLoggedIn(true);
        setFavorite(!!json?.data?.some((f) => f.storeId === storeId));
      })
      .catch(() => alive && setLoggedIn(false));
    return () => {
      alive = false;
    };
  }, [storeId]);

  const toggle = async () => {
    if (loggedIn === false) {
      router.push(`${ROUTES.auth.login}?callbackUrl=${encodeURIComponent(pathname ?? "/")}`);
      return;
    }
    if (busy || loggedIn === null) return;
    const next = !favorite;
    setFavorite(next);
    setBusy(true);
    try {
      const res = await fetch("/api/profile/favorites/stores", {
        method: next ? "POST" : "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: storeId }),
      });
      if (!res.ok) throw new Error(String(res.status));
      syncLocal(storeId, next);
      router.refresh();
    } catch {
      setFavorite(!next);
    } finally {
      setBusy(false);
    }
  };

  const label = favorite ? `Quitar ${storeName} de favoritos` : `Guardar ${storeName} en favoritos`;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      aria-pressed={favorite}
      title={label}
      className={cn(
        "absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-background/90 backdrop-blur-sm border shadow-xs flex items-center justify-center transition-transform hover:scale-110 active:scale-95",
        busy && "opacity-70"
      )}
    >
      <Heart
        className={cn(
          "w-5 h-5 transition-colors",
          favorite ? "fill-red-500 text-red-500" : "text-muted-foreground"
        )}
      />
    </button>
  );
}
